import { useCallback, useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Pagination, PaginationProps } from "src/components/ui/Pagination";

// Enhanced interfaces for better type safety
export interface PaginationConfig {
	initialPage?: number;
	pageSize?: number;
	total?: number;
	maxVisiblePages?: number;
	persistInSession?: boolean;
}

export interface PaginationState {
	currentPage: number;
	pageSize: number;
	total: number;
	totalPages: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	startIndex: number;
	endIndex: number;
	visiblePages: number[];
}

export interface PaginationActions {
	goToPage: (page: number) => void;
	goToNextPage: () => void;
	goToPreviousPage: () => void;
	goToFirstPage: () => void;
	goToLastPage: () => void;
	setPageSize: (size: number) => void;
	reset: () => void;
}

export interface UsePaginationReturn
	extends PaginationState,
		PaginationActions {
	isLoading: boolean;
	error: string | null;
	// Legacy API compatibility
	page: number;
	limit: number;
	disablePrevPage: boolean;
	disableNextPage: boolean;
	pageNumbers: number[];
	gotoPrevPage: () => void;
	gotoNextPage: () => void;
	Pagination: (props: any) => React.ReactElement | null;
}

// Enhanced hook with backward compatibility
export const usePagination = (
	initialState: any,
	paginationData?: PaginationProps
): UsePaginationReturn => {
	// Extract configuration from legacy API
	const {
		page: initialPage = 1,
		limit: initialLimit = 10,
		total = 10,
		setPageNo,
	} = initialState || {};

	// Enhanced state management
	const [currentPage, setCurrentPage] = useState(parseInt(initialPage));
	const [pageSize, setPageSize] = useState(parseInt(initialLimit));
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const location = useLocation();
	const pageKey = `${location.pathname}_page`;

	// Memoized calculations for better performance
	const totalPages = useMemo(() => {
		return paginationData?.totalPages && paginationData?.totalPages > 1
			? paginationData?.totalPages
			: Math.max(1, Math.ceil(total / pageSize));
	}, [paginationData?.totalPages, total, pageSize]);

	const hasNextPage = useMemo(
		() => currentPage < totalPages,
		[currentPage, totalPages]
	);
	const hasPreviousPage = useMemo(() => currentPage > 1, [currentPage]);

	const startIndex = useMemo(
		() => (currentPage - 1) * pageSize,
		[currentPage, pageSize]
	);
	const endIndex = useMemo(
		() => Math.min(startIndex + pageSize - 1, total - 1),
		[startIndex, pageSize, total]
	);

	// Generate visible page numbers with optimization
	const visiblePages = useMemo(() => {
		const pages: number[] = [];
		const maxVisible = 5; // Default max visible pages

		let startPage = Math.max(1, currentPage - 2);
		let endPage = Math.min(totalPages, startPage + maxVisible - 1);

		// Adjust start if we're near the end
		if (endPage - startPage < maxVisible - 1) {
			startPage = Math.max(1, endPage - maxVisible + 1);
		}

		for (let i = startPage; i <= endPage; i++) {
			pages.push(i);
		}

		return pages;
	}, [currentPage, totalPages]);

	// Legacy compatibility
	const disablePrevPage = !hasPreviousPage;
	const disableNextPage = !hasNextPage;

	// Load from session storage on mount with error handling
	useEffect(() => {
		try {
			const pageNo = sessionStorage.getItem(pageKey);
			if (pageNo) {
				const parsedPageNo = Number(pageNo);
				if (parsedPageNo >= 1 && parsedPageNo <= totalPages) {
					setCurrentPage(parsedPageNo);
				} else {
					setCurrentPage(1);
				}
			} else {
				setCurrentPage(1);
			}
		} catch (err) {
			console.warn(
				"Failed to load pagination state from session storage:",
				err
			);
			setCurrentPage(1);
		}
	}, [totalPages, pageKey]);

	// Save to session storage with error handling
	useEffect(() => {
		try {
			sessionStorage.setItem(pageKey, currentPage.toString());
			if (setPageNo) {
				setPageNo(currentPage);
			}
		} catch (err) {
			console.warn("Failed to save pagination state to session storage:", err);
		}
	}, [currentPage, setPageNo, pageKey]);

	// Enhanced navigation actions with error handling
	const goToPage = useCallback(
		(page: number) => {
			if (page < 1 || page > totalPages || page === currentPage) return;

			setIsLoading(true);
			setError(null);

			try {
				setCurrentPage(page);
			} catch (err) {
				setError("Failed to navigate to page");
			} finally {
				setIsLoading(false);
			}
		},
		[currentPage, totalPages]
	);

	const goToNextPage = useCallback(() => {
		if (hasNextPage) goToPage(currentPage + 1);
	}, [hasNextPage, currentPage, goToPage]);

	const goToPreviousPage = useCallback(() => {
		if (hasPreviousPage) goToPage(currentPage - 1);
	}, [hasPreviousPage, currentPage, goToPage]);

	const goToFirstPage = useCallback(() => {
		goToPage(1);
	}, [goToPage]);

	const goToLastPage = useCallback(() => {
		goToPage(totalPages);
	}, [goToPage, totalPages]);

	const changePageSize = useCallback(
		(size: number) => {
			if (size < 1) return;

			setIsLoading(true);
			setError(null);

			try {
				setPageSize(size);
				// Recalculate current page to maintain data position
				const newTotalPages = Math.ceil(total / size);
				const newCurrentPage = Math.min(currentPage, newTotalPages);
				setCurrentPage(Math.max(1, newCurrentPage));
			} catch (err) {
				setError("Failed to change page size");
			} finally {
				setIsLoading(false);
			}
		},
		[currentPage, total]
	);

	const reset = useCallback(() => {
		setCurrentPage(initialPage);
		setPageSize(initialLimit);
		setError(null);
		setIsLoading(false);
	}, [initialPage, initialLimit]);

	// Legacy API compatibility
	const page = currentPage;
	const limit = pageSize;
	const pageNumbers = visiblePages;

	// Legacy navigation functions
	const gotoPrevPage = goToPreviousPage;
	const gotoNextPage = goToNextPage;

	// Legacy pagination props for backward compatibility
	const paginationProps = {
		page,
		setPage: (page: any) => {
			const newPage = parseInt(page);
			if (newPage > 1) goToPage(newPage);
			else goToPage(1);
		},
		limit,
		setLimit: (limit: any) => {
			const newLimit = parseInt(limit);
			if (newLimit > 1) changePageSize(newLimit);
			else changePageSize(10);
		},
		total,
		disablePrevPage,
		disableNextPage,
		gotoPrevPage,
		gotoNextPage,
		pageNumbers,
		gotoFirstPage: goToFirstPage,
		gotoLastPage: goToLastPage,
		totalPages,
	};

	return {
		// Enhanced API
		currentPage,
		pageSize,
		total,
		totalPages,
		hasNextPage,
		hasPreviousPage,
		startIndex,
		endIndex,
		visiblePages,
		isLoading,
		error,
		goToPage,
		goToNextPage,
		goToPreviousPage,
		goToFirstPage,
		goToLastPage,
		setPageSize: changePageSize,
		reset,

		// Legacy API compatibility
		page,
		limit,
		disablePrevPage,
		disableNextPage,
		pageNumbers,
		gotoPrevPage,
		gotoNextPage,
		Pagination: (props: any) => Pagination({ ...paginationProps, ...props }),
	};
};

export default usePagination;
